import { isChanged, createProxy, getUntracked, trackMemo } from 'proxy-compare';
export { getUntracked, replaceNewProxy } from 'proxy-compare';

// This is required only for performance.
// https://github.com/dai-shi/proxy-memoize/issues/68
const targetCache = new WeakMap();
// constants from proxy-compare
const HAS_KEY_PROPERTY = 'h';
const ALL_OWN_KEYS_PROPERTY = 'w';
const HAS_OWN_KEY_PROPERTY = 'o';
const KEYS_PROPERTY = 'k';
const trackMemoOriginalObjSet = new WeakSet();
const isObject = x => typeof x === 'object' && x !== null;
const untrack = (x, seen) => {
  if (!isObject(x)) return x;
  const originalObj = getUntracked(x);
  if (originalObj !== null) {
    trackMemo(x);
    trackMemoOriginalObjSet.add(originalObj);
    return originalObj;
  }
  if (!seen.has(x)) {
    seen.add(x);
    Object.entries(x).forEach(([k, v]) => {
      const vv = untrack(v, seen);
      if (!Object.is(vv, v)) x[k] = vv;
    });
  }
  return x;
};
const touchAffected = (dst, src, affected) => {
  var _used$HAS_KEY_PROPERT, _used$HAS_OWN_KEY_PRO, _used$KEYS_PROPERTY;
  if (!isObject(dst) || !isObject(src)) return;
  if (trackMemoOriginalObjSet.has(getUntracked(src))) {
    trackMemo(dst);
    return;
  }
  const used = affected.get(getUntracked(src) || src);
  if (!used) return;
  (_used$HAS_KEY_PROPERT = used[HAS_KEY_PROPERTY]) == null || _used$HAS_KEY_PROPERT.forEach(key => {
    Reflect.has(dst, key);
  });
  if (used[ALL_OWN_KEYS_PROPERTY] === true) {
    Reflect.ownKeys(dst);
  }
  (_used$HAS_OWN_KEY_PRO = used[HAS_OWN_KEY_PROPERTY]) == null || _used$HAS_OWN_KEY_PRO.forEach(key => {
    Reflect.getOwnPropertyDescriptor(dst, key);
  });
  (_used$KEYS_PROPERTY = used[KEYS_PROPERTY]) == null || _used$KEYS_PROPERTY.forEach(key => {
    touchAffected(dst[key], src[key], affected);
  });
};
const isOriginalEqual = (x, y) => {
  for (let xx = x; xx; x = xx, xx = getUntracked(xx));
  for (let yy = y; yy; y = yy, yy = getUntracked(yy));
  return Object.is(x, y);
};
// properties
const OBJ_PROPERTY = 'o';
const RESULT_PROPERTY = 'r';
const AFFECTED_PROPERTY = 'a';
/**
 * Create a memoized function
 *
 * @example
 * import { memoize } from 'proxy-memoize';
 *
 * const fn = memoize(obj => ({ sum: obj.a + obj.b, diff: obj.a - obj.b }));
 *
 * @param options
 * @param options.size - (default: 1)
 * @param options.noWeakMap - disable tier-1 cache (default: false)
 */
function memoize(fn, options) {
  var _options$size;
  let memoListHead = 0;
  const size = (_options$size = options == null ? void 0 : options.size) != null ? _options$size : 1;
  const memoList = [];
  const resultCache = options != null && options.noWeakMap ? null : new WeakMap();
  const memoizedFn = obj => {
    const cache = resultCache == null ? void 0 : resultCache.get(obj);
    if (cache) {
      return cache;
    }
    for (let i = 0; i < size; i += 1) {
      const memo = memoList[(memoListHead + i) % size];
      if (!memo) break;
      const {
        [OBJ_PROPERTY]: memoObj,
        [AFFECTED_PROPERTY]: memoAffected,
        [RESULT_PROPERTY]: memoResult
      } = memo;
      if (!isChanged(memoObj, obj, memoAffected, new WeakMap(), isOriginalEqual)) {
        touchAffected(obj, memoObj, memoAffected);
        resultCache == null || resultCache.set(obj, memoResult);
        return memoResult;
      }
    }
    const affected = new WeakMap();
    const proxy = createProxy(obj, affected, undefined, targetCache);
    const result = untrack(fn(proxy), new WeakSet());
    touchAffected(obj, obj, affected);
    const entry = {
      [OBJ_PROPERTY]: obj,
      [RESULT_PROPERTY]: result,
      [AFFECTED_PROPERTY]: affected
    };
    memoListHead = (memoListHead - 1 + size) % size;
    memoList[memoListHead] = entry;
    resultCache == null || resultCache.set(obj, result);
    return result;
  };
  return memoizedFn;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

/**
 * Create a memoized function with args
 *
 * @example
 * import { memoizeWithArgs } from 'proxy-memoize';
 *
 * const fn = memoizeWithArgs((a, b) => ({ sum: a.v + b.v, diff: a.v - b.v }));
 *
 * @param options
 * @param options.size - (default: 1)
 */
const memoizeWithArgs = (fnWithArgs, options) => {
  const fn = memoize(args => fnWithArgs(...args), _extends({}, options, {
    noWeakMap: true
  }));
  return (...args) => fn(args);
};

export { memoize, memoizeWithArgs };
//# sourceMappingURL=index.modern.mjs.map
